'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Check,
  Circle,
  Ear,
  Mic,
  Play,
  RotateCcw,
  Sparkles,
  Square,
  Star,
  Volume2,
  X,
} from 'lucide-react';
import { canRecordVoice } from '@/lib/video/voice';
import {
  nachatZapis,
  proveritZvuk,
  vklyuchitMikrofon,
  vyklyuchitMikrofon,
  type IdushchayaZapis,
} from '@/lib/kursy/mikrofon';
import { raskodirovat } from '@/lib/kursy/intonaciya';
import { Volna } from './volna';
import { adresZvuka, golosaKursa, type Kurs, type Para, type Primer } from '@/lib/kursy/model';
import { najtiKurs, putKursa, putUroka } from '@/lib/kursy/razdel';
import {
  data,
  dobavitVremya,
  kluchPrimera,
  otmetit,
  otvetitPara,
  pustojProgress,
  zvezdy,
  type Ocenka,
  type Progress,
} from '@/lib/kursy/progress';
import {
  poprositHranit,
  prochitatProgress,
  prochitatZapisi,
  sohranitProgress,
  sohranitZapis,
} from '@/lib/kursy/hranilishche';
import { otkrytRazbor, voprosRazbora } from '@/lib/kursy/razbor';
import { golosaEtalona, golosaPary } from '@/lib/kursy/brak-ozvuchki';
import { ProverkaZapisi } from './proverka-zapisi';
import { slovaFrazy } from '@/lib/kursy/grafik-slova';
import {
  proshlaProverku,
  rezhimProverki,
  zagruzitEtalony,
  zagruzitPravila,
  type ItogProverki,
  type VremyaSlova,
} from '@/lib/kursy/proverka';
import { GrafikIntonacii } from './grafik-intonacii';
import { KartinkaKursa } from './kartinka-kursa';
import { GEROI, kartinka, kartinkaSlova } from '@/lib/kursy/kartinki';
import { AisLink as Link } from '@/components/aisearch/ais-link';

/** Сколько раз прозвучит каждая пара в «услышь разницу». */
const RAUNDOV_NA_PARU = 3;

export interface ElementPovtora {
  urok: string;
  primer: Primer;
}

type Shag =
  | { tip: 'start' }
  | { tip: 'para'; nomer: number; zvuchit: 'a' | 'b'; otvet?: 'a' | 'b' }
  | { tip: 'primer'; nomer: number }
  | { tip: 'itog' };

/** Главная кнопка текущего шага и остальные. */
const GLAVNAYA =
  'inline-flex items-center gap-2 rounded-full bg-[var(--color-accent-500)] px-5 py-2.5 font-semibold text-[var(--color-bg)] hover:bg-[var(--color-accent-400)]';
const VTORAYA =
  'inline-flex items-center gap-2 rounded-full border border-[var(--color-border)] px-5 py-2.5 hover:border-[var(--color-accent-500)]';

function igraj(src: string, onKonec?: () => void): void {
  const a = new Audio(src);
  if (onKonec) {
    a.onended = onKonec;
    // Нет файла — не держим человека: иначе запись не откроется никогда
    // (курс-черновик без озвучки, недокачанный офлайн).
    a.onerror = onKonec;
  }
  void a.play().catch(() => undefined);
}

/**
 * Чтение перед переводом: русскими буквами в скобках у сербского,
 * МФА в косых чертах у английского, ничего — если его нет (английские фразы).
 */
function chtenie(tekst: string, yazyk: string | undefined): string {
  if (!tekst) return '';
  return yazyk ? `${tekst} — ` : `[${tekst}] — `;
}

function slugPary(para: Para, storona: 'a' | 'b'): string {
  return `para-${para.id}-${storona}`;
}

function alfavitIzPamyati(): 'kir' | 'lat' {
  try {
    return localStorage.getItem('ais-kursy-alfavit') === 'lat' ? 'lat' : 'kir';
  } catch {
    return 'kir';
  }
}

/**
 * Тренажёр урока: сначала «услышь разницу» на парах, потом «повтори» по
 * каждому примеру — эталон несколькими голосами, своя запись, график
 * интонации и самооценка.
 *
 * С `povtor` вместо урока показывает подборку для повторения из разных
 * уроков курса: без пар и без итога урока.
 */
export function Trenazher({
  kursSlug,
  urokSlug,
  povtor,
  rech,
  onZakonchen,
}: {
  kursSlug: string;
  urokSlug?: string;
  povtor?: ElementPovtora[];
  /** Подборка povtor — тренировка речи, а не повторение трудного. */
  rech?: boolean;
  onZakonchen?: () => void;
}) {
  const kurs = najtiKurs(kursSlug) as Kurs;
  const urok = urokSlug ? kurs.uroki.find((u) => u.slug === urokSlug) : undefined;
  const elementy: ElementPovtora[] = useMemo(
    () => povtor ?? (urok ? urok.primery.map((primer) => ({ urok: urok.slug, primer })) : []),
    [povtor, urok],
  );
  // Каждая пара звучит несколько раз в случайном порядке сторон.
  const raundy = useMemo(() => {
    if (povtor || !urok) return [];
    return urok.pary.flatMap((para) =>
      Array.from({ length: RAUNDOV_NA_PARU }, () => ({
        para,
        zvuchit: Math.random() < 0.5 ? ('a' as const) : ('b' as const),
      })),
    );
  }, [povtor, urok]);

  const [progress, setProgress] = useState<Progress>(pustojProgress);
  const [shag, setShag] = useState<Shag>({ tip: 'start' });
  const [alfavitIzVybora, setAlfavit] = useState<'kir' | 'lat'>('kir');
  // Строгий режим: дальше только если автопроверка пропустила фразу.
  const [strogo, setStrogo] = useState(false);
  // У языка с одним письмом kir и lat совпадают, переключать нечего.
  const odnoPismo = kurs.pismo === 'odno';
  const alfavit = odnoPismo ? 'lat' : alfavitIzVybora;
  const yazyk = odnoPismo ? kurs.yazyk : undefined;
  const proshloeDejstvie = useRef(Date.now());

  useEffect(() => {
    setAlfavit(alfavitIzPamyati());
    try {
      setStrogo(localStorage.getItem('ais-kursy-strogo') === '1');
    } catch {
      // без памяти: обычный режим
    }
    void prochitatProgress(kursSlug).then(setProgress);
  }, [kursSlug]);

  // Микрофон включается при первой записи и держится до конца урока:
  // открытый заново, он полторы секунды отдаёт нули и съедает начало фразы.
  useEffect(
    () => () => {
      vyklyuchitMikrofon();
    },
    [],
  );

  /** Сохраняем сразу: вкладку могут закрыть посреди урока. */
  const obnovit = useCallback(
    (f: (p: Progress, segodnya: string) => Progress) => {
      const segodnya = data(new Date());
      // Время занятий: промежуток с прошлого действия, но не больше
      // полутора минут — отошедший от телефона человек не занимается.
      const sejchas = Date.now();
      const sekund = Math.min(90, Math.round((sejchas - proshloeDejstvie.current) / 1000));
      proshloeDejstvie.current = sejchas;
      setProgress((p) => {
        const novyj = dobavitVremya(f(p, segodnya), sekund, segodnya);
        void sohranitProgress(kursSlug, novyj);
        return novyj;
      });
    },
    [kursSlug],
  );

  const smenitAlfavit = () => {
    const novyj = alfavit === 'kir' ? 'lat' : 'kir';
    setAlfavit(novyj);
    try {
      localStorage.setItem('ais-kursy-alfavit', novyj);
    } catch {
      // без памяти о выборе: просто кириллица в следующий раз
    }
  };

  // Где человек остановился: первый пример урока, который он ещё ни разу
  // не отметил. Раньше урок каждый раз начинался с первого упражнения
  // (жалоба владельца 04.10). Пары «услышь разницу» при продолжении
  // пропускаются: они уже пройдены в первый заход.
  const prodolzhitS = povtor
    ? 0
    : elementy.findIndex((e) => !progress.otmetki[kluchPrimera(e.urok, e.primer.id)]);
  const mozhnoProdolzhit = prodolzhitS > 0;

  const nachat = (sNachala = true) => {
    proshloeDejstvie.current = Date.now();
    void poprositHranit();
    if (!sNachala && mozhnoProdolzhit) {
      setShag({ tip: 'primer', nomer: prodolzhitS });
      return;
    }
    const pervyj = raundy[0];
    if (pervyj) setShag({ tip: 'para', nomer: 0, zvuchit: pervyj.zvuchit });
    else setShag({ tip: 'primer', nomer: 0 });
  };

  const sleduyushchij = (posle: Shag) => {
    if (posle.tip === 'para') {
      const n = posle.nomer + 1;
      const r = raundy[n];
      if (r) setShag({ tip: 'para', nomer: n, zvuchit: r.zvuchit });
      else setShag({ tip: 'primer', nomer: 0 });
    } else if (posle.tip === 'primer') {
      const n = posle.nomer + 1;
      if (n < elementy.length) setShag({ tip: 'primer', nomer: n });
      else {
        setShag({ tip: 'itog' });
        onZakonchen?.();
      }
    }
  };

  if (elementy.length === 0) return null;
  const raund = shag.tip === 'para' ? raundy[shag.nomer] : undefined;
  const element = shag.tip === 'primer' ? elementy[shag.nomer] : undefined;

  return (
    <section
      aria-label="Тренажёр"
      className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg-card)] p-5 sm:p-7"
    >
      <div className="mb-5 flex items-center justify-between gap-3 text-sm text-[var(--color-text-muted)]">
        <span>
          {shag.tip === 'para' &&
            `Услышь разницу · ${String(shag.nomer + 1)} из ${String(raundy.length)}`}
          {shag.tip === 'primer' &&
            `Повтори · ${String(shag.nomer + 1)} из ${String(elementy.length)}`}
          {shag.tip === 'start' &&
            (povtor
              ? `${rech ? 'Тренировка речи' : 'Повторение'} · ${String(elementy.length)}`
              : 'Тренажёр')}
          {shag.tip === 'itog' && 'Готово'}
        </span>
        <label className="ml-auto inline-flex cursor-pointer items-center gap-1.5">
          <input
            type="checkbox"
            checked={strogo}
            onChange={(e) => {
              setStrogo(e.target.checked);
              try {
                localStorage.setItem('ais-kursy-strogo', e.target.checked ? '1' : '0');
              } catch {
                // без памяти
              }
            }}
            className="accent-[var(--color-accent-500)]"
          />
          Строго
        </label>
        {!odnoPismo && (
          <button
            type="button"
            onClick={smenitAlfavit}
            className="rounded-lg border border-[var(--color-border)] px-2.5 py-1 hover:border-[var(--color-accent-500)]"
            aria-label="Сменить алфавит"
          >
            {alfavit === 'kir' ? 'Кириллица' : 'Latinica'}
          </button>
        )}
      </div>

      {shag.tip !== 'start' && shag.tip !== 'itog' && (
        <div className="mb-6 h-1.5 overflow-hidden rounded-full bg-[var(--color-bg-elevated)]">
          <div
            className="h-full rounded-full bg-[var(--color-accent-500)] transition-all"
            style={{
              width: `${String(
                ((shag.tip === 'para' ? shag.nomer : raundy.length + shag.nomer) /
                  (raundy.length + elementy.length)) *
                  100,
              )}%`,
            }}
          />
        </div>
      )}

      {shag.tip === 'start' && (
        <div className="text-center">
          <p className="mb-5 text-[var(--color-text-muted)]">
            {rech
              ? 'Фразы из разных уроков вперемешку, каждый раз другой голос. Слушайте, повторяйте вслух и записывайте себя.'
              : povtor
                ? 'Слова, которые не получились или которые пора освежить.'
                : raundy.length > 0
                  ? 'Сначала научимся слышать разницу, потом повторять. Понадобятся звук и микрофон.'
                  : 'Слушайте эталон, записывайте себя и сравнивайте. Понадобятся звук и микрофон.'}
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {mozhnoProdolzhit && (
              <button
                type="button"
                onClick={() => {
                  nachat(false);
                }}
                className="rounded-xl bg-[var(--color-accent-500)] px-6 py-3 font-semibold text-[var(--color-bg)] hover:bg-[var(--color-accent-400)]"
              >
                Продолжить с {String(prodolzhitS + 1)} из {String(elementy.length)}
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                nachat(true);
              }}
              className={
                mozhnoProdolzhit
                  ? 'rounded-xl border border-[var(--color-border)] px-6 py-3 hover:border-[var(--color-accent-500)]'
                  : 'rounded-xl bg-[var(--color-accent-500)] px-6 py-3 font-semibold text-[var(--color-bg)] hover:bg-[var(--color-accent-400)]'
              }
            >
              {rech
                ? 'Начать'
                : povtor
                  ? 'Повторить'
                  : mozhnoProdolzhit
                    ? 'Начать сначала'
                    : 'Начать урок'}
            </button>
          </div>
        </div>
      )}

      {shag.tip === 'para' && raund && (
        <ShagPary
          key={shag.nomer}
          kurs={kurs.slug}
          yazyk={yazyk}
          urok={urokSlug ?? ''}
          para={raund.para}
          zvuchit={shag.zvuchit}
          onOtvet={(storona) => {
            obnovit((p, d) =>
              otvetitPara(
                p,
                kluchPrimera(urokSlug ?? '', raund.para.id),
                storona === shag.zvuchit,
                d,
              ),
            );
          }}
          onDalee={() => {
            sleduyushchij(shag);
          }}
        />
      )}

      {shag.tip === 'primer' && element && (
        <ShagPrimera
          key={`${element.urok}/${element.primer.id}`}
          kurs={kurs.slug}
          element={element}
          alfavit={alfavit}
          yazyk={yazyk}
          strogo={strogo}
          onOcenka={(o: Ocenka) => {
            obnovit((p, d) => otmetit(p, kluchPrimera(element.urok, element.primer.id), o, d));
            sleduyushchij(shag);
          }}
        />
      )}

      {shag.tip === 'itog' && (
        <Itog
          kurs={kurs}
          urokSlug={urokSlug}
          progress={progress}
          onZanovo={() => {
            setShag({ tip: 'start' });
          }}
        />
      )}
    </section>
  );
}

function ShagPary({
  kurs,
  urok,
  yazyk,
  para,
  zvuchit,
  onOtvet,
  onDalee,
}: {
  kurs: string;
  /** lang слов языка с одним письмом; без него — сербская кириллица. */
  yazyk?: string;
  urok: string;
  para: Para;
  zvuchit: 'a' | 'b';
  onOtvet: (storona: 'a' | 'b') => void;
  onDalee: () => void;
}) {
  const [otvet, setOtvet] = useState<'a' | 'b' | null>(null);
  // Голос каждый раз случайный: различать надо звук, а не диктора. Только
  // из проверенных для обоих слов пары: иначе «разница» была бы ошибкой синтеза.
  const golos = useMemo(() => {
    const vse = golosaKursa(najtiKurs(kurs) ?? {});
    const est = golosaPary(urok, slugPary(para, 'a'), slugPary(para, 'b'), vse);
    return est[Math.floor(Math.random() * est.length)] ?? 'g1';
  }, [kurs, urok, para]);
  const src = adresZvuka(kurs, urok, slugPary(para, zvuchit), golos);
  // Картинки — только если они есть у обоих слов: иначе одна из них подсказка.
  const sKartinkami = kartinkaSlova(kurs, para.a) !== null && kartinkaSlova(kurs, para.b) !== null;

  useEffect(() => {
    igraj(src);
  }, [src]);

  const vybrat = (s: 'a' | 'b') => {
    if (otvet) return;
    setOtvet(s);
    onOtvet(s);
  };

  return (
    <div className="text-center">
      <p className="mb-4 flex items-center justify-center gap-2 text-[var(--color-text-muted)]">
        <Ear className="h-5 w-5" aria-hidden="true" /> Какое слово прозвучало?
      </p>
      <button
        type="button"
        onClick={() => {
          igraj(src);
        }}
        className="mb-6 inline-flex items-center gap-2 rounded-full border border-[var(--color-border)] px-5 py-2.5 hover:border-[var(--color-accent-500)]"
      >
        <Volume2 className="h-5 w-5" /> Ещё раз
      </button>
      <div className="grid grid-cols-2 gap-3" lang={yazyk ?? 'sr-Cyrl'}>
        {(['a', 'b'] as const).map((s) => {
          const verno = otvet && s === zvuchit;
          const neverno = otvet === s && s !== zvuchit;
          return (
            <button
              key={s}
              type="button"
              onClick={() => {
                vybrat(s);
              }}
              className={`rounded-xl border-2 p-5 text-2xl font-bold transition-colors ${
                verno
                  ? 'border-[var(--color-success-500)]'
                  : neverno
                    ? 'border-[var(--color-danger-500)]'
                    : 'border-[var(--color-border)] hover:border-[var(--color-accent-500)]'
              }`}
            >
              {sKartinkami && (
                <KartinkaKursa
                  kurs={kurs}
                  id={`slovo-${s === 'a' ? para.a : para.b}`}
                  alt=""
                  className="mx-auto mb-3 !w-24 sm:!w-32"
                />
              )}
              {s === 'a' ? para.a : para.b}
            </button>
          );
        })}
      </div>
      {otvet && (
        <div className="mt-5">
          <p className="mb-4 text-[var(--color-text-muted)]">
            {otvet === zvuchit ? 'Верно! ' : 'Не то. '}
            {para.podskazka}
          </p>
          <button
            type="button"
            onClick={onDalee}
            className="rounded-xl bg-[var(--color-accent-500)] px-6 py-3 font-semibold text-[var(--color-bg)] hover:bg-[var(--color-accent-400)]"
          >
            Дальше
          </button>
        </div>
      )}
    </div>
  );
}

function ShagPrimera({
  kurs,
  element,
  alfavit,
  yazyk,
  strogo,
  onOcenka,
}: {
  kurs: string;
  element: ElementPovtora;
  alfavit: 'kir' | 'lat';
  yazyk?: string;
  /** Дальше только после пропуска автопроверкой (где она есть). */
  strogo?: boolean;
  onOcenka: (o: Ocenka) => void;
}) {
  const { primer, urok } = element;
  // Первый голос случайный: от примера к примеру звучат разные дикторы,
  // ухо привыкает к звуку, а не к одному тембру (HVPT).
  const [golos, setGolos] = useState(() => Math.floor(Math.random() * 16));
  const [zapis, setZapis] = useState<Blob | null>(null);
  const [pervaya, setPervaya] = useState<Blob | null>(null);
  const [idetZapis, setIdetZapis] = useState(false);
  /** Микрофон включается или дописывает хвост после «Стоп»: кнопка ждёт. */
  const [zhdem, setZhdem] = useState(false);
  const [analizator, setAnalizator] = useState<AnalyserNode | null>(null);
  const [oshibka, setOshibka] = useState<string | null>(null);
  /** Эталон дослушан хотя бы раз: можно переходить к записи. */
  const [poslushal, setPoslushal] = useState(false);
  const rec = useRef<IdushchayaZapis | null>(null);
  const kluch = `${kurs}/${kluchPrimera(urok, primer.id)}`;
  const golosa = useMemo(
    () => golosaEtalona(urok, primer.id, golosaKursa(najtiKurs(kurs) ?? {})),
    [kurs, urok, primer.id],
  );
  const etalon = adresZvuka(kurs, urok, primer.id, golosa[golos % golosa.length] ?? 'g1');
  const mozhnoPisat = useMemo(() => canRecordVoice(), []);
  // Автопроверка: вердикт (undefined — ещё нет, null — нет или недоступна),
  // где в записи слова, сколько раз не пропустила. И где слова в эталоне.
  const [itogProverki, setItogProverki] = useState<ItogProverki | null | undefined>(undefined);
  const [slovaZapisi, setSlovaZapisi] = useState<VremyaSlova[] | undefined>(undefined);
  const [nePropustila, setNePropustila] = useState(0);
  const [etalonySlov, setEtalonySlov] = useState<Record<string, VremyaSlova[]> | null>(null);
  const [estProverka, setEstProverka] = useState(false);
  useEffect(() => {
    void zagruzitEtalony(kurs).then((e) => {
      setEtalonySlov(e?.[`${urok}/${primer.id}`] ?? null);
    });
    void zagruzitPravila(kurs).then((p) => {
      // Строгий режим — у любой фразы с правилами: даже без проверки звуков
      // модель узнаёт, сказана ли фраза целиком.
      setEstProverka(p?.frazy[`${urok}/${primer.id}`] !== undefined);
    });
  }, [kurs, urok, primer.id]);
  const golosSejchas = golosa[golos % golosa.length] ?? 'g1';
  const strogijShag = strogo === true && estProverka && rezhimProverki() !== null;
  const propustila = proshlaProverku(itogProverki ?? null);

  const poslushat = (src: string) => {
    igraj(src, () => {
      setPoslushal(true);
    });
  };

  useEffect(() => {
    poslushat(etalon);
    // Только при смене примера: смена голоса играет по нажатию.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void prochitatZapisi(kluch).then((z) => {
      // «Как было» показываем, только если первая запись старше суток:
      // сравнивать с записью пятиминутной давности незачем.
      if (z.pervaya && Date.now() - z.pervaya.kogda > 86_400_000) setPervaya(z.pervaya.zvuk);
    });
  }, [kluch]);

  const sleduyushchijGolos = () => {
    const n = (golos + 1) % golosa.length;
    setGolos(n);
    poslushat(adresZvuka(kurs, urok, primer.id, golosa[n] ?? 'g1'));
  };

  const zapisat = async () => {
    setOshibka(null);
    if (idetZapis && rec.current) {
      setZhdem(true);
      const f = await rec.current.stop();
      rec.current = null;
      setIdetZapis(false);
      setZhdem(false);
      const zvuk = await raskodirovat(f);
      const itog = zvuk ? proveritZvuk(zvuk.dannye, zvuk.chastota) : 'da';
      if (itog !== 'da') {
        setOshibka(
          itog === 'tiho'
            ? 'Мы ничего не услышали. Говорите ближе к микрофону: когда вы говорите, волна должна двигаться.'
            : 'Запись слишком короткая. Скажите всю фразу и только потом нажмите «Стоп».',
        );
        return;
      }
      setItogProverki(undefined);
      setSlovaZapisi(undefined);
      setZapis(f);
      void sohranitZapis(kluch, f);
      return;
    }
    setZhdem(true);
    try {
      const m = await vklyuchitMikrofon();
      setAnalizator(m.analizator);
      rec.current = await nachatZapis(m);
      setIdetZapis(true);
    } catch (e) {
      setOshibka(
        e instanceof Error && e.message === 'микрофон молчит'
          ? 'Микрофон включён, но не передаёт звук. Проверьте, что выбран нужный микрофон и он не выключен.'
          : 'Нет доступа к микрофону. Разрешите его в настройках браузера или просто повторяйте вслух.',
      );
    } finally {
      setZhdem(false);
    }
  };

  // Шаг: 1 — слушаем эталон, 2 — записываем (или повторяем вслух без
  // микрофона), 3 — сравниваем и оцениваем.
  const etap: 1 | 2 | 3 = zapis ? 3 : poslushal ? (mozhnoPisat ? 2 : 3) : 1;
  const podskazka = zhdem
    ? idetZapis
      ? 'Дописываем конец фразы…'
      : 'Включаем микрофон, подождите…'
    : idetZapis
      ? 'Говорите! Произнесите фразу и нажмите «Стоп».'
      : etap === 1
        ? 'Послушайте, как звучит фраза. Не расслышали — нажмите «Эталон» ещё раз или «Другой голос».'
        : etap === 2
          ? 'Теперь ваша очередь: нажмите «Записать себя», скажите фразу вслух и нажмите «Стоп».'
          : zapis
            ? 'Послушайте свою запись и сравните с эталоном. На графике жёлтая линия — ваша мелодия, синяя — эталона: чем больше они похожи по форме, тем лучше.'
            : 'Повторите фразу вслух несколько раз, как в эталоне, и оцените, похоже ли получилось.';

  const slushat = (z: Blob) => {
    const url = URL.createObjectURL(z);
    const a = new Audio(url);
    a.onended = () => {
      URL.revokeObjectURL(url);
    };
    void a.play().catch(() => undefined);
  };

  return (
    <div>
      <div className="mb-5 text-center">
        <p
          className="text-3xl font-bold sm:text-4xl"
          lang={yazyk ?? (alfavit === 'kir' ? 'sr-Cyrl' : 'sr-Latn')}
        >
          {(() => {
            const t = primer.fraza ?? primer;
            return alfavit === 'kir' ? t.kir : t.lat;
          })()}
        </p>
        <p className="mt-2 text-[var(--color-text-muted)]">
          {chtenie((primer.fraza ?? primer).chtenie, yazyk)}
          {(primer.fraza ?? primer).perevod}
        </p>
        {primer.udarenie && yazyk && (
          <p className="mt-1 text-sm text-[var(--color-text-muted)]" lang={yazyk}>
            Ударение: {primer.udarenie}
          </p>
        )}
        {primer.fraza && (
          <p className="mt-1 text-sm text-[var(--color-text-muted)]">
            Слово:{' '}
            <span className="font-semibold text-[var(--color-text)]" lang={yazyk ?? 'sr'}>
              {alfavit === 'kir' ? primer.kir : primer.lat}
            </span>{' '}
            {chtenie(primer.chtenie, yazyk)}
            {primer.perevod}
          </p>
        )}
      </div>

      {/* Шаги: человек всегда видит, что делать сейчас (жалоба владельца 03.10:
          «вообще не понял, что надо»). */}
      <ol className="mb-3 grid grid-cols-3 gap-2 text-center text-xs sm:text-sm">
        {[
          'Послушайте',
          mozhnoPisat ? 'Запишите себя' : 'Повторите вслух',
          'Сравните и оцените',
        ].map((t, i) => (
          <li
            key={t}
            className={`rounded-lg px-2 py-1.5 ${
              etap === i + 1
                ? 'bg-[var(--color-accent-500)] font-semibold text-[var(--color-bg)]'
                : etap > i + 1
                  ? 'text-[var(--color-success-400)]'
                  : 'text-[var(--color-text-muted)]'
            }`}
          >
            {etap > i + 1 ? '✓' : `${String(i + 1)}.`} {t}
          </li>
        ))}
      </ol>
      <p className="mb-5 min-h-[3rem] text-center text-[var(--color-text)]" aria-live="polite">
        {podskazka}
      </p>

      <Geroj kurs={kurs} golos={golosSejchas} />
      <div className="mb-5 flex flex-wrap justify-center gap-3">
        <button
          type="button"
          onClick={() => {
            poslushat(etalon);
          }}
          className={etap === 1 ? GLAVNAYA : VTORAYA}
        >
          <Play className="h-5 w-5" /> Эталон
        </button>
        <button type="button" onClick={sleduyushchijGolos} className={VTORAYA}>
          <RotateCcw className="h-5 w-5" /> Другой голос
        </button>
        {mozhnoPisat && (
          <button
            type="button"
            onClick={() => void zapisat()}
            disabled={zhdem}
            className={
              idetZapis
                ? 'inline-flex items-center gap-2 rounded-full bg-[var(--color-danger-500)] px-5 py-2.5 font-semibold text-white'
                : etap === 2
                  ? GLAVNAYA
                  : VTORAYA
            }
          >
            {idetZapis ? <Square className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
            {idetZapis ? 'Стоп' : zapis ? 'Записать ещё' : 'Записать себя'}
          </button>
        )}
        {pervaya && (
          <button
            type="button"
            onClick={() => {
              slushat(pervaya);
            }}
            className={`${VTORAYA} text-[var(--color-text-muted)]`}
          >
            <Circle className="h-4 w-4" /> Как было в первый раз
          </button>
        )}
      </div>
      {zapis && (
        <p className="-mt-2 mb-4 flex justify-center gap-4 text-xs">
          <button
            type="button"
            onClick={() => {
              slushat(zapis);
            }}
            className="inline-flex items-center gap-1 text-[var(--color-text-muted)] underline hover:text-[var(--color-accent-400)]"
          >
            <Volume2 className="h-3.5 w-3.5" /> Послушать мою запись
          </button>
          <button
            type="button"
            onClick={() => {
              const url = URL.createObjectURL(zapis);
              const a = document.createElement('a');
              a.href = url;
              a.download = `zapis-${primer.id}.${zapis.type.includes('mp4') ? 'm4a' : zapis.type.includes('ogg') ? 'ogg' : 'webm'}`;
              a.click();
              URL.revokeObjectURL(url);
            }}
            className="text-[var(--color-text-muted)] underline hover:text-[var(--color-accent-400)]"
          >
            Скачать мою запись
          </button>
        </p>
      )}
      {oshibka && (
        <p className="mb-4 text-center text-sm text-[var(--color-warning-400)]">{oshibka}</p>
      )}

      {(idetZapis || zhdem) && (
        <div className="mb-5">
          <Volna analizator={analizator} aktivna={idetZapis} />
        </div>
      )}

      {zapis && (
        <ProverkaZapisi
          kurs={kurs}
          urok={urok}
          id={primer.id}
          zapis={zapis}
          onItog={(itog, slova) => {
            setItogProverki(itog);
            setSlovaZapisi(slova);
            if (itog && !proshlaProverku(itog)) setNePropustila((n) => n + 1);
          }}
        />
      )}

      <GrafikIntonacii
        etalon={etalon}
        zapis={zapis}
        slovaEtalona={etalonySlov?.[golosSejchas]}
        slovaZapisi={slovaZapisi}
        tekstySlov={slovaFrazy((primer.fraza ?? primer).lat)}
        pokazatVydelennoe={urok === 'intonaciya'}
      />

      {strogijShag ? (
        <div className="mt-6 text-center" aria-live="polite">
          {!zapis ? (
            <p className="text-[var(--color-text-muted)]">
              Строгий режим: дальше — когда автопроверка пропустит фразу.
            </p>
          ) : itogProverki === undefined ? (
            <p className="text-[var(--color-text-muted)]">Ждём автопроверку…</p>
          ) : itogProverki === null ? (
            // Проверка недоступна: не держим человека, оценивает сам.
            <button
              type="button"
              onClick={() => {
                onOcenka('pochti');
              }}
              className={GLAVNAYA}
            >
              Проверка недоступна — дальше
            </button>
          ) : propustila ? (
            <button
              type="button"
              onClick={() => {
                onOcenka('ok');
              }}
              className={GLAVNAYA}
            >
              <Check className="h-5 w-5" /> Проверка пропустила — дальше
            </button>
          ) : (
            <>
              <p className="mb-2 font-semibold">
                Проверка не пропустила. Послушайте эталон и запишите ещё раз.
              </p>
              {/* Модель иногда ошибается: после трёх попыток можно пройти дальше. */}
              {nePropustila >= 3 && (
                <button
                  type="button"
                  onClick={() => {
                    onOcenka('net');
                  }}
                  className="text-sm text-[var(--color-text-muted)] underline hover:text-[var(--color-accent-400)]"
                >
                  Пропустить фразу, вернусь к ней позже
                </button>
              )}
            </>
          )}
        </div>
      ) : (
        <>
          <p
            className={`mb-3 mt-6 text-center ${etap === 3 ? 'font-semibold text-[var(--color-text)]' : 'text-[var(--color-text-muted)]'}`}
          >
            Похоже на эталон? Оцените себя честно
          </p>
          <div className={`grid grid-cols-3 gap-3 ${etap === 3 ? '' : 'opacity-60'}`}>
            <button
              type="button"
              onClick={() => {
                onOcenka('net');
              }}
              className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-[var(--color-border)] p-3 hover:border-[var(--color-danger-500)]"
            >
              <X className="h-4 w-4" /> Не вышло
            </button>
            <button
              type="button"
              onClick={() => {
                onOcenka('pochti');
              }}
              className="rounded-xl border border-[var(--color-border)] p-3 hover:border-[var(--color-warning-500)]"
            >
              Почти
            </button>
            <button
              type="button"
              onClick={() => {
                onOcenka('ok');
              }}
              className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-[var(--color-border)] p-3 hover:border-[var(--color-success-500)]"
            >
              <Check className="h-4 w-4" /> Получилось
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function Itog({
  kurs,
  urokSlug,
  progress,
  onZanovo,
}: {
  kurs: Kurs;
  urokSlug?: string;
  progress: Progress;
  onZanovo: () => void;
}) {
  const nomer = kurs.uroki.findIndex((u) => u.slug === urokSlug);
  const urok = nomer >= 0 ? kurs.uroki[nomer] : undefined;
  const sled = nomer >= 0 ? kurs.uroki[nomer + 1] : undefined;
  const z = urok ? zvezdy(progress, urok) : 0;

  return (
    <div className="text-center">
      {urok && (
        <>
          <div className="mb-3 flex justify-center gap-1" aria-label={`Звёзд: ${String(z)} из 3`}>
            {[1, 2, 3].map((i) => (
              <Star
                key={i}
                className={`h-9 w-9 ${i <= z ? 'fill-[var(--color-warning-400)] text-[var(--color-warning-400)]' : 'text-[var(--color-border)]'}`}
              />
            ))}
          </div>
          <p className="mb-2 text-lg font-semibold">
            {z > 0 ? urok.cel : 'Хорошее начало. Повторите урок — звёзды придут.'}
          </p>
        </>
      )}
      <p className="mb-6 text-[var(--color-text-muted)]">Очков всего: {progress.ochki}</p>
      <div className="flex flex-wrap justify-center gap-3">
        <button
          type="button"
          onClick={onZanovo}
          className="rounded-xl border border-[var(--color-border)] px-5 py-3 hover:border-[var(--color-accent-500)]"
        >
          Ещё раз
        </button>
        {sled ? (
          <Link
            href={putUroka(kurs, sled)}
            className="rounded-xl bg-[var(--color-accent-500)] px-5 py-3 font-semibold text-[var(--color-bg)] hover:bg-[var(--color-accent-400)]"
          >
            Следующий урок: {sled.nazvanie}
          </Link>
        ) : (
          <Link
            href={putKursa(kurs)}
            className="rounded-xl bg-[var(--color-accent-500)] px-5 py-3 font-semibold text-[var(--color-bg)] hover:bg-[var(--color-accent-400)]"
          >
            К курсу
          </Link>
        )}
      </div>
      {urok && (
        <div className="mt-8 border-t border-[var(--color-border)] pt-6">
          <button
            type="button"
            onClick={() => {
              const neVyshlo = urok.primery
                .filter((p) => progress.otmetki[kluchPrimera(urok.slug, p.id)]?.posled !== 'ok')
                .map((p) => p.kir);
              otkrytRazbor(voprosRazbora(kurs, urok, neVyshlo));
            }}
            className="inline-flex items-center gap-2 rounded-xl border border-[var(--color-accent-500)] px-5 py-3 font-medium hover:bg-[var(--color-bg-elevated)]"
          >
            <Sparkles className="h-5 w-5" /> Разобрать урок с ИИ
          </button>
          <p className="mt-2 text-xs text-[var(--color-text-muted)]">
            Платная функция, нужен вход. Вопрос с вашими трудными словами откроется в чате —
            отправите его сами.
          </p>
        </div>
      )}
    </div>
  );
}

/** Кто сейчас говорит эталон: лицо голоса, если у курса есть герои. */
function Geroj({ kurs, golos }: { kurs: string; golos: string }) {
  const g = GEROI[golos];
  if (!g || !kartinka(kurs, `geroj-${golos}`)) return null;
  return (
    <p className="mb-4 flex items-center justify-center gap-3 text-sm text-[var(--color-text-muted)]">
      <span className="w-12">
        <KartinkaKursa kurs={kurs} id={`geroj-${golos}`} alt="" className="!rounded-full" />
      </span>
      <span>
        Говорит <span className="font-semibold text-[var(--color-text)]">{g.imya}</span>, {g.kto}
      </span>
    </p>
  );
}
